/** The pricing methods the engine understands. Constraining `pricing_method`
 *  to this union (rather than a bare `string`) makes a typo or a retired/legacy
 *  method (e.g. 'Sliding', 'Casement', the removed 'Column') a COMPILE error in
 *  the TS catalog source, instead of silently pricing to $0 at runtime
 *  (unknown method → 0). Area = sqft × rate w/ floor; Panel/Set/Piece/Trip =
 *  flat rate × qty; POA = rep-entered flat amount (add-on only). */
export type PricingMethod = 'Area' | 'Panel' | 'Set' | 'Piece' | 'POA' | 'Trip';

export interface Product {
  cat1: string;
  cat2: string;
  description: string;
  prd_code: string;
  quote_desc: string;
  pricing_method: PricingMethod;
  uom: string;
  unit: string;
  min_area: number | null;
  base_rate: number;
  min_price: number | null;
  oversize_applies: boolean;
  oversize_rate_addition: number | null;
  oversize_rate: number | null;
  wholesale_rate?: number;
  wholesale_min_price?: number | null;
  wholesale_oversize_rate_addition?: number | null;
  wholesale_oversize_rate?: number | null;
  /** Optional list of selectable colour / finish options for this product
   *  (e.g. ['White', 'Black', 'Bronze', 'Wood-grain']). Authored in the
   *  catalog admin. When non-empty, the quote line editor shows a colour
   *  dropdown; absent/empty means no colour choice. */
  colours?: string[];
  /** When true, a cart consisting ONLY of exempt products is billed at face
   *  value — no minimum-order top-up, no delivery fee, no CBD surcharge. Used
   *  by the Measurement Fee. Absent/false = a normal order-charge-bearing product. */
  exemptFromOrderCharges?: boolean;
}

// Reactive version counter for the mutable PRODUCTS array.
// See CLAUDE.md architecture invariant — PRODUCTS is spliced in-place, so
// components need a separate signal to re-render when the catalog changes.
let catalogVersion = 0;
const catalogListeners = new Set<() => void>();

export const bumpCatalogVersion = () => {
  catalogVersion++;
  catalogListeners.forEach((cb) => cb());
};
export const subscribeCatalog = (cb: () => void) => {
  catalogListeners.add(cb);
  return () => {
    catalogListeners.delete(cb);
  };
};
export const getCatalogVersion = () => catalogVersion;

export const PRODUCTS: Product[] = [
  {
    cat1: 'Mozzy Net Window',
    cat2: 'Base',
    description: 'Mozzy Net only (Window W800 - H1300)',
    prd_code: 'MZN-001',
    quote_desc: 'Mozzy Net only - Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 110,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Mozzy Net Window',
    cat2: 'Base',
    description: 'UV Blinds only (Window W800 - H1300)',
    prd_code: 'MZN-002',
    quote_desc: 'UV Blinds only - Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 110,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Mozzy Net Window',
    cat2: 'Base',
    description: '2in1 Mozzy Net + UV Blinds (Window W900 - H1300)',
    prd_code: 'MZN-003',
    quote_desc: '2in1 Mozzy Net + UV Blinds - Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 145,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Mozzy Net Pro Window',
    cat2: 'Base',
    description: 'Mozzy Net PRO (Window W800 - H1300) - Lock or Magnet',
    prd_code: 'MZN-004',
    quote_desc: 'Mozzy Net PRO - Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 180,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Dark Grey', 'White'],
  },
  {
    cat1: 'Mozzy Net Pro Window',
    cat2: 'Base',
    description: 'UV Blinds PRO (Window W800 - H1300) - Lock or Magnet',
    prd_code: 'MZN-005',
    quote_desc: 'UV Blinds PRO - Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 210,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Dark Grey', 'White'],
  },
  {
    cat1: 'Mozzy Net Pro Window',
    cat2: 'Base',
    description: '2in 1 PRO Mozzy Net + UV Blinds (Window W900 - H1300) Lock or Magnet',
    prd_code: 'MZN-006',
    quote_desc: '2in1 PRO Mozzy Net + UV Blinds - Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 250,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Dark Grey', 'White'],
  },
  {
    cat1: 'Mozzy Net Door',
    cat2: 'Base',
    description: 'Mozzy Net only (Door W900 - H2300) - Magnet',
    prd_code: 'MZN-007',
    quote_desc: 'Mozzy Net only - Door',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 250,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Mozzy Net Door',
    cat2: 'Base',
    description: 'UV Blinds only (Door W900 - H2300) - Magnet',
    prd_code: 'MZN-008',
    quote_desc: 'UV Blinds only - Door',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 250,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Mozzy Net Door',
    cat2: 'Base',
    description: '2in1 Mozzy Net + UV Blinds (Door W900 - H2300) - Magnet',
    prd_code: 'MZN-009',
    quote_desc: '2in1 Mozzy Net + UV Blinds - Door',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 360,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Mozzy Net Pro Door',
    cat2: 'Base',
    description: 'Mozzy Net PRO (Door W900 - H2300) - Lock or Magnet',
    prd_code: 'MZN-010',
    quote_desc: 'Mozzy Net PRO - Door',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 520,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Dark Grey', 'White'],
  },
  {
    cat1: 'Mozzy Net Pro Door',
    cat2: 'Base',
    description: 'UV Blinds only (Door W900 - H2300) - Lock or Magnet',
    prd_code: 'MZN-011',
    quote_desc: 'UV Blinds PRO - Door',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 520,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Dark Grey', 'White'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Base',
    description: 'Flexi Window - Float Glass',
    prd_code: 'AWN-001',
    quote_desc: 'Flexi Window',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 45,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Base',
    description: 'Casement Window - Float Glass',
    prd_code: 'AWN-002',
    quote_desc: 'Casement Window',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 21,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Base',
    description: 'Fixed Panel - Float Glass',
    prd_code: 'AWN-003',
    quote_desc: 'Fixed Panel',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 21,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Base',
    description: 'Sliding Window (2way track) - Float Glass',
    prd_code: 'AWN-004',
    quote_desc: 'Sliding Window (2way track)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 19,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Base',
    description: 'Sliding Window (3way track) - Float Glass',
    prd_code: 'AWN-005',
    quote_desc: 'Sliding Window (3way track)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 20,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Top Hung Window (top or bottom) - Clear Laminated Glass',
    prd_code: 'AWN-006',
    quote_desc: 'Top Hung Window (top or bottom)',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 85,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Fixed Window (top or bottom) - Clear Laminated Glass',
    prd_code: 'AWN-007',
    quote_desc: 'Fixed Window (top or bottom)',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 85,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Tempered Glass',
    prd_code: 'AWN-008',
    quote_desc: 'Tempered Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 11,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Laminated Glass',
    prd_code: 'AWN-009',
    quote_desc: 'Laminated Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: '9.52mm Laminated Glass',
    prd_code: 'AWN-010',
    quote_desc: '9.52mm Laminated Glass',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 150,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: '13.52mm Laminated Glass',
    prd_code: 'AWN-011',
    quote_desc: '13.52mm Laminated Glass',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 200,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: '16mm Double Glazed Glass',
    prd_code: 'AWN-012',
    quote_desc: '16mm Double Glazed Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 15,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Low-Iron Glass',
    prd_code: 'AWN-013',
    quote_desc: 'Low-Iron Glass',
    pricing_method: 'POA',
    uom: 'Sqft',
    unit: '/ poa',
    min_area: null,
    base_rate: 0,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Low-E Glass',
    prd_code: 'AWN-014',
    quote_desc: 'Low-E Glass',
    pricing_method: 'POA',
    uom: 'Sqft',
    unit: '/ poa',
    min_area: null,
    base_rate: 0,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Powder Coating',
    prd_code: 'AWN-015',
    quote_desc: 'Powder Coating',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 4,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Double-Decker',
    prd_code: 'AWN-016',
    quote_desc: 'Double-Decker',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Alpha Material',
    prd_code: 'AWN-017',
    quote_desc: 'Alpha Material',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 15,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Window 3/4 Wall High Box (BCA)',
    prd_code: 'AWN-018',
    quote_desc: 'Window 3/4 Wall High Box (BCA)',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ col',
    min_area: null,
    base_rate: 250,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Lattice Design',
    prd_code: 'AWN-019',
    quote_desc: 'Lattice Design',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 12,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Handle with key  code: 4316D',
    prd_code: 'AWN-020',
    quote_desc: 'Handle with key',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ pc',
    min_area: null,
    base_rate: 60,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Window',
    cat2: 'Add-on',
    description: 'Multi Lock  code: A5200-95 No Key / A5200-423 with Key',
    prd_code: 'AWN-021',
    quote_desc: 'Multi Lock',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ pc',
    min_area: null,
    base_rate: 90,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Base',
    description: '1col Top Hung Window (24"x24") - WSL390 (White Laminated Glass)',
    prd_code: 'TWN-001',
    quote_desc: '1col Top Hung Window (24"x24")',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 220,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Base',
    description: '2col Top Hung Window (48"x24") - WSL390 (White Laminated Glass)',
    prd_code: 'TWN-002',
    quote_desc: '2col Top Hung Window (48"x24")',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 330,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Base',
    description: '3col Top Hung Window (60"x24") - WSL390 (White Laminated Glass)',
    prd_code: 'TWN-003',
    quote_desc: '3col Top Hung Window (60"x24")',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 440,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: 'Wire Glass',
    prd_code: 'TWN-004',
    quote_desc: 'Wire Glass',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 50,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: 'Powder Coating',
    prd_code: 'TWN-005',
    quote_desc: 'Powder Coating',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 50,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: '6" Exhaust Fan with hole',
    prd_code: 'TWN-006',
    quote_desc: '6" Exhaust Fan with hole',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 195,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: 'Moderate Height Work (1.5m - 2.5m)',
    prd_code: 'TWN-007',
    quote_desc: 'Moderate Height Work (1.5m - 2.5m)',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 120,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: 'Surcharge for installing only 1 set in single trip',
    prd_code: 'TWN-008',
    quote_desc: 'Single-set install surcharge',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 100,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: 'Fan customer provide, WSS install',
    prd_code: 'TWN-009',
    quote_desc: 'Fan (customer provide, WSS install)',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 100,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Base',
    description: '1col Louver Window (24"x24") - Float Glass',
    prd_code: 'TWN-010',
    quote_desc: '1col Louver Window (24"x24")',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 180,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Base',
    description: '2col Louver Window (48"x24") - Float Glass',
    prd_code: 'TWN-011',
    quote_desc: '2col Louver Window (48"x24")',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 240,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Base',
    description: '3col Louver Window (60"x24") - Float Glass',
    prd_code: 'TWN-012',
    quote_desc: '3col Louver Window (60"x24")',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 300,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Toilet Window',
    cat2: 'Add-on',
    description: 'Wire Glass',
    prd_code: 'TWN-013',
    quote_desc: 'Wire Glass (per pc)',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ pc',
    min_area: null,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Interlocking Door (wall to wall) - Clear Laminated Glass',
    prd_code: 'SSL-001',
    quote_desc: 'Interlocking Door (wall to wall)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 36,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Interlocking Door (track extensions) - Clear Laminated Glass',
    prd_code: 'SSL-002',
    quote_desc: 'Interlocking Door (track extensions)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 40,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Folding Door - Centre Fold - Clear Laminated Glass',
    prd_code: 'SSL-003',
    quote_desc: 'Folding Door - Centre Fold',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 36,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Folding Door - Side Fold - Clear Laminated Glass',
    prd_code: 'SSL-004',
    quote_desc: 'Folding Door - Side Fold',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 40,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Spirit Door - Clear Laminated Glass',
    prd_code: 'SSL-005',
    quote_desc: 'Spirit Door',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 40,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Swing Door - Clear Tempered Glass',
    prd_code: 'SSL-006',
    quote_desc: 'Swing Door',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 38,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Double Door - Clear Laminated Glass',
    prd_code: 'SSL-007',
    quote_desc: 'Double Door',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 40,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Base',
    description: 'Invisible Door - Clear Laminated Glass',
    prd_code: 'SSL-008',
    quote_desc: 'Invisible Door',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 40,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Tempered Clear Glass',
    prd_code: 'SSL-009',
    quote_desc: 'Tempered Clear Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 3,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Moru Tempered Clear Glass',
    prd_code: 'SSL-010',
    quote_desc: 'Moru Tempered Clear Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 5,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Tinted Tempered Glass',
    prd_code: 'SSL-011',
    quote_desc: 'Tinted Tempered Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 8,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Bronze', 'Euro Grey', 'Green'],
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Low-Iron Clear Glass',
    prd_code: 'SSL-012',
    quote_desc: 'Low-Iron Clear Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'White Laminated Glass (WSL390)',
    prd_code: 'SSL-013',
    quote_desc: 'White Laminated Glass (WSL390)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 3,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Frosted Sticker',
    prd_code: 'SSL-014',
    quote_desc: 'Frosted Sticker',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 6,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Mirror Clear Glass with Black or White Composite Panel',
    prd_code: 'SSL-015',
    quote_desc: 'Mirror Clear Glass with Black or White Composite Panel',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 350,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Powder Coating',
    prd_code: 'SSL-016',
    quote_desc: 'Powder Coating',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 4,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Lattice Design',
    prd_code: 'SSL-017',
    quote_desc: 'Lattice Design',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 13,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Outer Frame (2 side)',
    prd_code: 'SSL-018',
    quote_desc: 'Outer Frame (2 side)',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 320,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Magnet Lock (1side) + Outer Frame',
    prd_code: 'SSL-019',
    quote_desc: 'Magnet Lock (1side) + Outer Frame',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ side',
    min_area: null,
    base_rate: 300,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Slimness Door',
    cat2: 'Add-on',
    description: 'Built-in Handle',
    prd_code: 'SSL-020',
    quote_desc: 'Built-in Handle',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ pc',
    min_area: null,
    base_rate: 100,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Window Grille',
    cat2: 'Base',
    description: 'Window Grille - 2way Sliding - Hollow Tubes -  WS306',
    prd_code: 'WGL-001',
    quote_desc: 'Window Grille - 2way Sliding',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Window Grille',
    cat2: 'Base',
    description: 'Window Grille - 3way Sliding - Hollow Tubes -  WS306',
    prd_code: 'WGL-002',
    quote_desc: 'Window Grille - 3way Sliding',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 13,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Window Grille',
    cat2: 'Base',
    description: 'Window Grille - Swing - Hollow Tubes -  WS306',
    prd_code: 'WGL-003',
    quote_desc: 'Window Grille - Swing',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 15,
    base_rate: 12,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Window Grille',
    cat2: 'Add-on',
    description: 'Powder Coating',
    prd_code: 'WGL-004',
    quote_desc: 'Powder Coating',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 4,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Window Grille',
    cat2: 'Add-on',
    description: 'Inner Gap 2" to 2"1/2 (Cat prevention grille)',
    prd_code: 'WGL-005',
    quote_desc: 'Inner Gap 2"-2.5" (Cat prevention)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 4,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Window Grille',
    cat2: 'Add-on',
    description: 'Hollow Tube ( Horizontal / Vertical ) for Door along Corridor',
    prd_code: 'WGL-006',
    quote_desc: 'Hollow Tube (Horizontal / Vertical)',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 60,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Base',
    description:
      'Sliding Door - Top and Bottom track (1/2/3 way track) B5 Lock - Float Glass or PVC',
    prd_code: 'ASD-001',
    quote_desc: 'Sliding Door - Top and Bottom track',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 27,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Alpha Material (55mm x 28mm)',
    prd_code: 'ASD-002',
    quote_desc: 'Alpha Material (55mm x 28mm)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 13,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Big Material (76mm x 28mm)',
    prd_code: 'ASD-003',
    quote_desc: 'Big Material (76mm x 28mm)',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 15,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Top Hung / Fixed Window - Clear Laminated Glass',
    prd_code: 'ASD-004',
    quote_desc: 'Top Hung / Fixed Window',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 110,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Hanging Door',
    prd_code: 'ASD-005',
    quote_desc: 'Hanging Door',
    pricing_method: 'Panel',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 80,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Multipoint Lock code: YM5 without Key / YM6 with Key',
    prd_code: 'ASD-006',
    quote_desc: 'Multipoint Lock (YM5 / YM6)',
    pricing_method: 'Set',
    uom: 'Set',
    unit: '/ set',
    min_area: null,
    base_rate: 120,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Tempered Glass',
    prd_code: 'ASD-007',
    quote_desc: 'Tempered Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 11,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Laminated Glass',
    prd_code: 'ASD-008',
    quote_desc: 'Laminated Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: '16mm Double Glazed Glass',
    prd_code: 'ASD-009',
    quote_desc: '16mm Double Glazed Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 15,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Low-Iron Glass',
    prd_code: 'ASD-010',
    quote_desc: 'Low-Iron Glass',
    pricing_method: 'POA',
    uom: 'Sqft',
    unit: '/ poa',
    min_area: null,
    base_rate: 0,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Sliding Door',
    cat2: 'Add-on',
    description: 'Low-E Glass',
    prd_code: 'ASD-011',
    quote_desc: 'Low-E Glass',
    pricing_method: 'POA',
    uom: 'Sqft',
    unit: '/ poa',
    min_area: null,
    base_rate: 0,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Base',
    description: 'Swing Door - 1 Panel - Float Glass',
    prd_code: 'ASW-001',
    quote_desc: 'Swing Door - 1 Panel',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 42,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Base',
    description: '3" Louver Swing Door - 1 Panel',
    prd_code: 'ASW-002',
    quote_desc: '3" Louver Swing Door - 1 Panel',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: 21,
    base_rate: 52,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    colours: ['Black', 'White', 'Natural Anodised', 'Bronze Anodised'],
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Add-on',
    description: 'Tempered Glass',
    prd_code: 'ASW-003',
    quote_desc: 'Tempered Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 11,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Add-on',
    description: 'Laminated Glass',
    prd_code: 'ASW-004',
    quote_desc: 'Laminated Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 10,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Add-on',
    description: '16mm Double Glazed Glass',
    prd_code: 'ASW-005',
    quote_desc: '16mm Double Glazed Glass',
    pricing_method: 'Area',
    uom: 'Sqft',
    unit: '/ sqft',
    min_area: null,
    base_rate: 15,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Add-on',
    description: 'Low-Iron Glass',
    prd_code: 'ASW-006',
    quote_desc: 'Low-Iron Glass',
    pricing_method: 'POA',
    uom: 'Sqft',
    unit: '/ poa',
    min_area: null,
    base_rate: 0,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Aluminium Swing Door',
    cat2: 'Add-on',
    description: 'Low-E Glass',
    prd_code: 'ASW-007',
    quote_desc: 'Low-E Glass',
    pricing_method: 'POA',
    uom: 'Sqft',
    unit: '/ poa',
    min_area: null,
    base_rate: 0,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - CBD Delivery and/or Installation trip surcharge',
    prd_code: 'SER-001',
    quote_desc: 'CBD Delivery Surchard',
    pricing_method: 'Trip',
    uom: 'Trip',
    unit: '/ each',
    min_area: null,
    base_rate: 50,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 50,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - Labour Charge for Carrying Up Goods via Staircase',
    prd_code: 'SER-002',
    quote_desc: 'Delivery via Stairs',
    pricing_method: 'Piece',
    uom: 'Panel',
    unit: '/ panel',
    min_area: null,
    base_rate: 500,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 500,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - Dismantling of Iron and Wood Windows - Sq Ft',
    prd_code: 'SER-003',
    quote_desc: 'Dismantling of Iron and Wood Windows',
    pricing_method: 'Area',
    uom: 'Sq Ft',
    unit: '/ sq ft^15',
    min_area: 15,
    base_rate: 10,
    min_price: 150,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 10,
    wholesale_min_price: 150,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - Supply & Erection of Aluminium Scaffolding at Site',
    prd_code: 'SER-004',
    quote_desc: 'Supply & Erection of Aluminium Scaffolding at Site',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ each',
    min_area: null,
    base_rate: 600,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 600,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - PE application & endorsement',
    prd_code: 'SER-005',
    quote_desc: 'PE application & endorsement',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ each',
    min_area: null,
    base_rate: 6000,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 6000,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - PE endorsement admin fee',
    prd_code: 'SER-006',
    quote_desc: 'PE endorsement admin fee',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ each',
    min_area: null,
    base_rate: 800,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 800,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Surcharge',
    description: 'Service - Cancellation of Acted Work Order (Before Production)',
    prd_code: 'SER-007',
    quote_desc: 'Cancellation of Order (Abortive Work)',
    pricing_method: 'Piece',
    uom: 'Piece',
    unit: '/ each',
    min_area: null,
    base_rate: 80,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 80,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
  },
  {
    cat1: 'Service',
    cat2: 'Service',
    description: 'On-site Measurement Fee',
    prd_code: 'SER-008',
    quote_desc: 'Measurement Fee',
    pricing_method: 'Piece',
    uom: 'each',
    unit: '/ each',
    min_area: null,
    base_rate: 150,
    min_price: null,
    oversize_applies: false,
    oversize_rate_addition: null,
    oversize_rate: null,
    wholesale_rate: 150,
    wholesale_min_price: null,
    wholesale_oversize_rate_addition: null,
    wholesale_oversize_rate: null,
    exemptFromOrderCharges: true,
  },
];
